import type { SubscriptionRef } from '@visious/core';
import { AUTH_QUERY_KEY, fetchSubscriptions, type AuthApiProblem } from '../api/auth';
import { getPlatform } from '../platform/instance';

/**
 * The subscription list's query, shared: the Subscriptions page renders it, and every
 * subscribe/unsubscribe button reads it to answer "am I subscribed to this channel?". One key means
 * one cache — a button and the page can never disagree about the same list.
 *
 * Under {@link AUTH_QUERY_KEY} (the convention with teeth: signing out drops the prefix), keyed by
 * the grant's own timestamp so a refusal cached against the previous token is not served to the new
 * one.
 */
export function subscriptionsListKey(grantKey: number): readonly unknown[] {
  return [AUTH_QUERY_KEY, 'subscriptions', grantKey];
}

/** A query that failed is a value here, not a throw — the transport returns its errors (1c). */
export type SubscriptionsResult =
  | { readonly ok: true; readonly subscriptions: readonly SubscriptionRef[] }
  | { readonly ok: false; readonly problem: AuthApiProblem };

/**
 * `null` scopes, deliberately — the same call the sign-in page makes and for the same reason it
 * gives: a page that pre-refuses its own request reports a scope gap it *inferred* rather than one
 * that happened. The instance is the authority, and `api/problem.ts` has a sentence for each of the
 * five ways it says no.
 */
export async function loadSubscriptions(): Promise<SubscriptionsResult> {
  const boot = await getPlatform();
  if (!boot.ok) return { ok: false, problem: { kind: 'bad-url', path: '/api/v1/auth/subscriptions' } };

  const result = await fetchSubscriptions(boot.platform, null);
  return result.ok ? { ok: true, subscriptions: result.value.items } : { ok: false, problem: result.error };
}
